// src/pages/ProductIssues/ProductIssues.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  Divider,
  InputAdornment,
  LinearProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Build as BuildIcon,
  Print as PrintIcon,
  GetApp as DownloadIcon,
  Refresh as RefreshIcon,
  Keyboard as KeyboardIcon,
  Memory as MemoryIcon,
  BatteryChargingFull as BatteryIcon,
  Wifi as WifiIcon,
  Speaker as SpeakerIcon,
  Whatshot as OverheatIcon,
  BugReport as SoftwareIcon,
  Build as HardwareIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getProductIssues, createProductIssue, updateProductIssueStatus, deleteProductIssue, getProductIssuesStats, getProducts } from '../../services/api';

const ProductIssues = () => {
  const navigate = useNavigate();
  const [issues, setIssues] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [stats, setStats] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    product_id: '',
    serial_number: '',
    issue_type: 'hardware_issue',
    issue_description: '',
    severity: 'medium',
    reported_by: '',
    notes: '',
    repair_cost: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchIssues(),
        fetchProducts(),
        fetchStats()
      ]);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchIssues = async () => {
    try {
      const response = await getProductIssues();
      if (response.success) {
        setIssues(response.data || []);
      }
    } catch (err) {
      setError(err.message || 'Failed to load issues');
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await getProducts();
      if (response.success) {
        setProducts(response.data || []);
      }
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await getProductIssuesStats();
      if (response.success) {
        setStats(response.data || {});
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleOpenDialog = () => {
    setFormData({
      product_id: '',
      serial_number: '',
      issue_type: 'hardware_issue',
      issue_description: '',
      severity: 'medium',
      reported_by: localStorage.getItem('erp_username') || 'System User',
      notes: '',
      repair_cost: ''
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setError(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.product_id) {
      setError('Please select a product');
      return;
    }
    if (!formData.serial_number.trim()) {
      setError('Serial number is required');
      return;
    }
    if (!formData.issue_description.trim()) {
      setError('Issue description is required');
      return;
    }

    try {
      setLoading(true);
      const dataToSend = {
        ...formData,
        repair_cost: parseFloat(formData.repair_cost) || 0
      };
      
      const response = await createProductIssue(dataToSend);
      if (response.success) {
        setSuccess('Issue reported successfully');
        fetchIssues();
        fetchStats();
        handleCloseDialog();
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError(err.message || 'Failed to create issue');
    } finally {
      setLoading(false);
    }
  };

  const handleViewIssue = (issue) => {
    setSelectedIssue(issue);
    setViewDialogOpen(true);
  };

  const handleUpdateStatus = (issue) => {
    setSelectedIssue(issue);
    setFormData(prev => ({
      ...prev,
      status: issue.status
    }));
    setStatusDialogOpen(true);
  };

  const handleStatusUpdate = async () => {
    if (!selectedIssue) return;

    try {
      setLoading(true);
      const response = await updateProductIssueStatus(selectedIssue.id, {
        status: formData.status,
        resolution_notes: formData.resolution_notes,
        repair_cost: parseFloat(formData.repair_cost) || 0
      });
      
      if (response.success) {
        setSuccess(`Issue status updated to ${formData.status}`);
        fetchIssues();
        fetchStats();
        setStatusDialogOpen(false);
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError(err.message || 'Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteIssue = async (id) => {
    if (!window.confirm('Are you sure you want to delete this issue?')) return;

    try {
      setLoading(true);
      const response = await deleteProductIssue(id);
      if (response.success) {
        setSuccess('Issue deleted successfully');
        fetchIssues();
        fetchStats();
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError(err.message || 'Failed to delete issue');
    } finally {
      setLoading(false);
    }
  };

  const getIssueTypeIcon = (issueType) => {
    const iconMap = {
      'keyboard_issue': <KeyboardIcon />,
      'motherboard_issue': <MemoryIcon />,
      'screen_issue': <ViewIcon />,
      'battery_issue': <BatteryIcon />,
      'charging_issue': <BatteryIcon />,
      'wifi_issue': <WifiIcon />,
      'speaker_issue': <SpeakerIcon />,
      'fan_issue': <HardwareIcon />,
      'overheating': <OverheatIcon />,
      'software_issue': <SoftwareIcon />,
      'hardware_issue': <HardwareIcon />,
      'physical_damage': <WarningIcon />,
      'other': <WarningIcon />
    };
    return iconMap[issueType] || <WarningIcon />;
  };

  const getIssueTypeLabel = (issueType) => {
    const labelMap = {
      'keyboard_issue': 'Keyboard Issue',
      'motherboard_issue': 'Motherboard Issue',
      'screen_issue': 'Screen Issue',
      'battery_issue': 'Battery Issue',
      'charging_issue': 'Charging Issue',
      'wifi_issue': 'WiFi Issue',
      'speaker_issue': 'Speaker Issue',
      'fan_issue': 'Fan Issue',
      'overheating': 'Overheating',
      'software_issue': 'Software Issue',
      'hardware_issue': 'Hardware Issue',
      'physical_damage': 'Physical Damage',
      'other': 'Other Issue'
    };
    return labelMap[issueType] || issueType;
  };

  const getStatusChip = (status) => {
    const statusMap = {
      'pending': { color: 'warning', icon: <PendingIcon />, label: 'Pending' },
      'in_progress': { color: 'info', icon: <BuildIcon />, label: 'In Progress' },
      'resolved': { color: 'success', icon: <CheckCircleIcon />, label: 'Resolved' },
      'cannot_fix': { color: 'error', icon: <WarningIcon />, label: 'Cannot Fix' }
    };
    const s = statusMap[status] || statusMap.pending;
    return (
      <Chip
        icon={s.icon}
        label={s.label}
        color={s.color}
        size="small"
      />
    );
  };

  const getSeverityChip = (severity) => {
    const severityMap = {
      'low': { color: 'success', label: 'Low' },
      'medium': { color: 'warning', label: 'Medium' },
      'high': { color: 'error', label: 'High' },
      'critical': { color: 'error', label: 'Critical', variant: 'filled' }
    };
    const s = severityMap[severity] || severityMap.medium;
    return (
      <Chip
        label={s.label}
        color={s.color}
        size="small"
        variant={s.variant || 'outlined'}
      />
    );
  };

  if (loading && issues.length === 0) {
    return (
      <Box sx={{ width: '100%', p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/products')}
            sx={{ mb: 1 }}
          >
            Back to Products
          </Button>
          <Typography variant="h4" sx={{ color: '#2c3e50', fontWeight: 'bold' }}>
            Product Issues Management
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Track and manage product issues by serial number
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            color="success"
            startIcon={<DownloadIcon />}
            onClick={() => window.open('/api/product-issues/export/csv', '_blank')}
          >
            Export CSV
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
            sx={{
              backgroundColor: '#e74c3c',
              '&:hover': { backgroundColor: '#c0392b' }
            }}
          >
            Report Issue
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Issues
              </Typography>
              <Typography variant="h4">
                {stats.total_issues || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#fff3e0' }}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Pending
              </Typography>
              <Typography variant="h4" color="warning.main">
                {stats.by_status?.find(s => s.status === 'pending')?.count || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#e3f2fd' }}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                In Progress
              </Typography>
              <Typography variant="h4" color="info.main">
                {stats.by_status?.find(s => s.status === 'in_progress')?.count || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#e8f5e8' }}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Resolved
              </Typography>
              <Typography variant="h4" color="success.main">
                {stats.by_status?.find(s => s.status === 'resolved')?.count || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Issues Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>Product</strong></TableCell>
              <TableCell><strong>Serial Number</strong></TableCell>
              <TableCell><strong>Issue Type</strong></TableCell>
              <TableCell><strong>Description</strong></TableCell>
              <TableCell><strong>Severity</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Reported By</strong></TableCell>
              <TableCell><strong>Reported Date</strong></TableCell>
              <TableCell><strong>Repair Cost</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {issues.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No issues reported. Click "Report Issue" to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              issues.map((issue) => (
                <TableRow key={issue.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {issue.product?.name || 'N/A'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {issue.product?.product_code || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={issue.serial_number}
                      size="small"
                      variant="outlined"
                      sx={{ fontFamily: 'monospace' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getIssueTypeIcon(issue.issue_type)}
                      <Typography variant="body2">
                        {getIssueTypeLabel(issue.issue_type)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={issue.issue_description}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {issue.issue_description}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{getSeverityChip(issue.severity)}</TableCell>
                  <TableCell>{getStatusChip(issue.status)}</TableCell>
                  <TableCell>{issue.reported_by}</TableCell>
                  <TableCell>
                    {new Date(issue.reported_date).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {issue.repair_cost ? `LKR ${issue.repair_cost}` : '-'}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewIssue(issue)}
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {issue.status !== 'resolved' && issue.status !== 'cannot_fix' && (
                        <Tooltip title="Update Status">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleUpdateStatus(issue)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {issue.status === 'pending' && (
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteIssue(issue.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Report Issue Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>Report Product Issue</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Select Product *</InputLabel>
                <Select
                  name="product_id"
                  value={formData.product_id}
                  onChange={handleChange}
                  label="Select Product *"
                >
                  {products.map(product => (
                    <MenuItem key={product.id} value={product.id}>
                      {product.name} ({product.product_code}) - Stock: {product.good_quantity || 0} good / {product.issue_quantity || 0} issues
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Serial Number *"
                name="serial_number"
                value={formData.serial_number}
                onChange={handleChange}
                placeholder="e.g., SN00123456"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Issue Type *</InputLabel>
                <Select
                  name="issue_type"
                  value={formData.issue_type}
                  onChange={handleChange}
                  label="Issue Type *"
                >
                  <MenuItem value="keyboard_issue">Keyboard Issue</MenuItem>
                  <MenuItem value="motherboard_issue">Motherboard Issue</MenuItem>
                  <MenuItem value="screen_issue">Screen Issue</MenuItem>
                  <MenuItem value="battery_issue">Battery Issue</MenuItem>
                  <MenuItem value="charging_issue">Charging Issue</MenuItem>
                  <MenuItem value="wifi_issue">WiFi Issue</MenuItem>
                  <MenuItem value="speaker_issue">Speaker Issue</MenuItem>
                  <MenuItem value="fan_issue">Fan Issue</MenuItem>
                  <MenuItem value="overheating">Overheating</MenuItem>
                  <MenuItem value="software_issue">Software Issue</MenuItem>
                  <MenuItem value="hardware_issue">Hardware Issue</MenuItem>
                  <MenuItem value="physical_damage">Physical Damage</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Issue Description *"
                name="issue_description"
                value={formData.issue_description}
                onChange={handleChange}
                multiline
                rows={3}
                placeholder="Describe the issue in detail..."
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Severity</InputLabel>
                <Select
                  name="severity"
                  value={formData.severity}
                  onChange={handleChange}
                  label="Severity"
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Repair Cost (LKR)"
                name="repair_cost"
                type="number"
                value={formData.repair_cost}
                onChange={handleChange}
                InputProps={{
                  startAdornment: <InputAdornment position="start">LKR</InputAdornment>,
                  inputProps: { min: 0, step: 0.01 }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reported By"
                name="reported_by"
                value={formData.reported_by}
                onChange={handleChange}
                placeholder="Your name"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Additional Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                multiline
                rows={2}
                placeholder="Any additional information..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="warning"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Report Issue'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Issue Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        {selectedIssue && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {getIssueTypeIcon(selectedIssue.issue_type)}
                <Typography variant="h6">
                  Issue Details: {selectedIssue.product?.name}
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Serial Number"
                    value={selectedIssue.serial_number}
                    InputProps={{ readOnly: true }}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Issue Type"
                    value={getIssueTypeLabel(selectedIssue.issue_type)}
                    InputProps={{ readOnly: true }}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={selectedIssue.issue_description}
                    InputProps={{ readOnly: true }}
                    multiline
                    rows={3}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Severity"
                    value={selectedIssue.severity}
                    InputProps={{ readOnly: true }}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Status"
                    value={selectedIssue.status}
                    InputProps={{ readOnly: true }}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Repair Cost"
                    value={selectedIssue.repair_cost ? `LKR ${selectedIssue.repair_cost}` : 'Not set'}
                    InputProps={{ readOnly: true }}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Reported By"
                    value={selectedIssue.reported_by}
                    InputProps={{ readOnly: true }}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Reported Date"
                    value={new Date(selectedIssue.reported_date).toLocaleString()}
                    InputProps={{ readOnly: true }}
                    margin="normal"
                  />
                </Grid>
                {selectedIssue.resolved_date && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Resolved Date"
                      value={new Date(selectedIssue.resolved_date).toLocaleString()}
                      InputProps={{ readOnly: true }}
                      margin="normal"
                    />
                  </Grid>
                )}
                {selectedIssue.resolved_by && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Resolved By"
                      value={selectedIssue.resolved_by}
                      InputProps={{ readOnly: true }}
                      margin="normal"
                    />
                  </Grid>
                )}
                {selectedIssue.resolution_notes && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Resolution Notes"
                      value={selectedIssue.resolution_notes}
                      InputProps={{ readOnly: true }}
                      multiline
                      rows={2}
                      margin="normal"
                    />
                  </Grid>
                )}
                {selectedIssue.notes && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Additional Notes"
                      value={selectedIssue.notes}
                      InputProps={{ readOnly: true }}
                      multiline
                      rows={2}
                      margin="normal"
                    />
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="sm" fullWidth>
        {selectedIssue && (
          <>
            <DialogTitle>Update Issue Status</DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={formData.status || selectedIssue.status}
                      onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                      label="Status"
                    >
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="in_progress">In Progress</MenuItem>
                      <MenuItem value="resolved">Resolved</MenuItem>
                      <MenuItem value="cannot_fix">Cannot Fix</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Repair Cost (LKR)"
                    type="number"
                    value={formData.repair_cost || selectedIssue.repair_cost || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, repair_cost: e.target.value }))}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">LKR</InputAdornment>,
                      inputProps: { min: 0, step: 0.01 }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Resolution Notes"
                    multiline
                    rows={3}
                    value={formData.resolution_notes || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, resolution_notes: e.target.value }))}
                    placeholder="Add notes about the resolution..."
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleStatusUpdate}
                variant="contained"
                color="primary"
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Update Status'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default ProductIssues;